import React, { useEffect, useRef, useState, useMemo } from "react";
import { IonPage } from "@ionic/react";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Card, Statistic, Progress, Typography, Space, Row, Col, Table, notification } from "antd";
import { PlusOutlined } from "@ant-design/icons";

//
import log from "../../../utils/log";
import c from "../../../utils/common";
import moment from "moment";
import metaData from "../../../data/metaData";

//
// import ProductStatus from "../../../components/table/ProductStatus";
import ActionHeader from "../../../components/Table/ActionHeader";
import DelaySearch from "../../../components/Table/DelaySearch";
import FilterDropdown from "../../../components/Table/FIlterDropdown";

//
import useAuthStore from "../../../states/useAuth";
import Meta from "../../../components/Meta";
import ProductPeriod from "../../../components/Table/ProductPeriod";
import { getProductAvailableQty } from "../../../utils/product";
import useProductQuery from "../../../hooks/useQuery/useProduct";
import ProductTag from "../../../components/Table/ProductTag";

const { Title, Text } = Typography;

const Product = () => {
  const history = useHistory();
  const location = useLocation();

  const { auth, user } = useAuthStore();

  const {
    data: products = [],
    isFetching: isFetchingProducts,
    refetch: refetchProducts,
  } = useProductQuery({
    companyId: user?.companyId,
    populate: {
      path: "brand",
      select: "name logo",
    },
  });
  log.debug(products);

  //Filter
  const [searchValue, setSearchValue] = useState();

  //Table
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [pagination, setPagination] = useState(1);
  const [filteredDataCount, setFilteredDataCount] = useState(products.length);

  const pageRef = useRef();

  const filterData = (searchValue, data) => {
    if (!searchValue) return data;

    const lowerSearchValue = searchValue.toLowerCase();

    return data.filter((record) => {
      const id = record?.id?.toLowerCase() || "";
      const productName = record?.name?.toLowerCase() || "";

      return id.includes(lowerSearchValue) || productName.includes(lowerSearchValue);
    });
  };

  const filteredData = useMemo(() => {
    // Filter data based on searchValue
    let filtered = filterData(searchValue, products);

    return filtered.sort((a, b) => {
      // Sort by status first (pending over approved), then by lastModify in descending order
      // if (a.status !== b.status) {
      //   return a.status === "pending" ? -1 : 1;
      // }
      return new Date(b.time.lastModify) - new Date(a.time.lastModify);
    });
  }, [products, searchValue]);

  const columns = [
    // {
    //   title: "RequestId",
    //   dataIndex: "id",
    //   key: "id",
    //   render: (text, record) => <Paragraph copyable={{ text: record?.id }}>{record?.id}</Paragraph>,
    //   sorter: (a, b) => (a?.id).localeCompare(b?.id),
    // },
    {
      title: "Product",
      dataIndex: "name",
      key: "name",
      width: 350,
      render: (text, record) => (
        <Meta
          image={
            record?.gallery?.[0] ? (
              <img
                style={{ width: 75, objectFit: "cover", marginRight: "0.5rem" }}
                src={metaData.blobPreURL + "clientUpload-" + record.gallery[0] + ".jpeg" + metaData.blobPostURL}
              />
            ) : null
          }
          title={<Title level={5}>{record?.name}</Title>}
          description={<ProductTag product={record} />}
          maxWidth={200}
        />
      ),
      sorter: (a, b) => (a?.name).localeCompare(b?.name),
    },
    {
      title: "Available",
      dataIndex: "qty",
      key: "qty",
      width: 75,
      render: (text, record) => {
        return (
          <Text strong>
            {record?.type == "discount"
              ? "unlimited"
              : getProductAvailableQty(record?.qty) > 0
              ? getProductAvailableQty(record?.qty)
              : "--"}
          </Text>
        );
      },
    },
    {
      title: "For Sale",
      dataIndex: "qty.sale",
      key: "qty.sale",
      width: 75,
      render: (text, record) => {
        return (
          <Text strong>{record?.type == "discount" ? "--" : record?.qty?.sale > 0 ? record?.qty?.sale : "--"}</Text>
        );
      },
    },
    {
      title: "Coin",
      dataIndex: "qty.estamp",
      key: "qty.estamp",
      width: 75,
      render: (text, record) => {
        return (
          <Text strong>{record?.type == "discount" ? "--" : record?.qty?.estamp > 0 ? record?.qty?.estamp : "--"}</Text>
        );
      },
    },
    {
      title: "Code",
      dataIndex: "qty.promotionCode",
      key: "qty.promotionCode",
      width: 75,
      render: (text, record) => {
        return (
          <Text strong>
            {record?.type == "discount" ? "--" : record?.qty?.promotionCode > 0 ? record?.qty?.promotionCode : "--"}
          </Text>
        );
      },
    },
    {
      title: "Welcome",
      dataIndex: "qty.welcome",
      key: "qty.welcome",
      width: 75,
      render: (text, record) => {
        return (
          <Text strong>
            {record?.type == "discount" ? "--" : record?.qty?.welcome > 0 ? record?.qty?.welcome : "--"}
          </Text>
        );
      },
    },
    {
      title: "Referral",
      dataIndex: "qty.referral",
      key: "qty.referral",
      width: 75,
      render: (text, record) => {
        return (
          <Text strong>
            {record?.type == "discount" ? "--" : record?.qty?.referral > 0 ? record?.qty?.referral : "--"}
          </Text>
        );
      },
    },
    {
      title: "",
      dataIndex: "time",
      key: "time",
      width: 350,
      render: (text, record) => <ProductPeriod time={record?.time} />,
    },
    // {
    //   title: "Status",
    //   dataIndex: "isDisable",
    //   key: "isDisable",
    //   width: 200,
    //   render: (text, record) => <ProductStatus product={record} />,
    //   sorter: (a, b) => (a?.isDisable).localeCompare(b?.isDisable),
    // },
    {
      title: "Action",
      key: "action",
      width: 75,
      fixed: "right",
      render: (text, record) => (
        <Space
          style={{
            fontSize: "1rem",
          }}
        >
          <a
            onClick={() => {
              history.push("/merchantUser/product/" + record?.id);
            }}
          >
            detail
          </a>
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys) => setSelectedRowKeys(newSelectedRowKeys),
  };

  const handleActionClick = (key) => {
    log.debug(key);
  };

  // Handle pagination change (page number or page size change)
  const handleTableChange = (pagination, filters, sorter, extra) => {
    const { current, pageSize } = pagination;
    console.log(current);
    setPagination(current);
    scrollToTop();

    setFilteredDataCount(extra.currentDataSource.length);
  };

  const scrollToTop = () => {
    if (pageRef.current) {
      pageRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div style={{ margin: "0 auto" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Title level={3}>Product</Title>
        <div>
          <Button
            size="large"
            type="primary"
            icon={<PlusOutlined />}
            style={{ marginRight: "8px" }}
            onClick={() => {
              history.push("/merchantUser/product/request/new");
            }}
          >
            Add Product
          </Button>
        </div>
      </div>

      <Row justify="space-between" style={{ width: "100%", marginBottom: "2rem" }}>
        <DelaySearch
          style={{ width: "20rem" }}
          placeholder="Search product name / ID"
          onSearch={(value) => {
            log.debug(value);
            setSearchValue(value);
          }}
          isDelay={false}
        />
      </Row>

      {/* Filter Group */}
      {/* {activeSegment === "all" ? (
        <Card bordered style={{ marginBottom: "20px", background: "var(--ion-color-step-50)" }}>
          <Row>
            <FilterDropdown
              label="Status"
              defaultValue="all"
              options={[
                { value: "all", label: "All Status" },
                { value: "pending", label: "Pending" },
                { value: "approved", label: "Approved" },
                { value: "rejected", label: "Rejected" },
              ]}
            />
          </Row>
        </Card>
      ) : null} */}

      <ActionHeader
        pagination={pagination}
        pageSize={20}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
        count={filteredDataCount}
        onActionClick={handleActionClick}
      />

      <Table
        className="white-table-header"
        // rowSelection={rowSelection}
        rowSelection={undefined} //disabled multiple selection
        sticky
        columns={columns}
        loading={isFetchingProducts}
        dataSource={filteredData}
        scroll={{ x: 1600 }} // Adjust based on total column width
        pagination={{
          current: pagination,
          pageSize: 20,
          position: ["none", "bottomCenter"],
          showSizeChanger: false,
        }}
        onChange={handleTableChange} // This handles the next/prev page
      />
    </div>
  );
};

export default Product;
